import { useState, useRef } from "react";
import { getEnvVariables } from "../../helpers";
import { elevenLabsOptions } from "../data";

const { elevenlabsApiKey, nodeEnv } = getEnvVariables();
const IS_PROD = nodeEnv === "production";

export const useSpeechSynthesis = () => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const audioRef = useRef(null);

  const playAudio = (audioUrl, onEndCallback, shouldRevoke = false) => {
    stopSpeaking();
    setIsSpeaking(true);

    audioRef.current = new Audio(audioUrl);
    audioRef.current.onended = () => {
      setIsSpeaking(false);
      if (shouldRevoke) URL.revokeObjectURL(audioUrl);
      if (onEndCallback) onEndCallback();
    };
    audioRef.current.onerror = () => {
      setIsSpeaking(false);
      if (shouldRevoke) URL.revokeObjectURL(audioUrl);
    };

    audioRef.current.play().catch((error) => {
      if (error.name !== "AbortError") {
        console.error("Error playing audio:", error);
      }
      setIsSpeaking(false);
      if (shouldRevoke) URL.revokeObjectURL(audioUrl);
    });
  };

  const speakText = async (botMessage, onEndCallback) => {
    const { content, context = {}, audioUrl, mode = null } = botMessage;
    const { clarification = null } = context;

    if (!content) return;

    if (audioUrl) {
      playAudio(audioUrl, onEndCallback);
      return;
    }

    const shouldUseElevenLabs =
      IS_PROD &&
      ((mode === "answering_questionnaire" && clarification) ||
        mode !== "answering_questionnaire");

    if (shouldUseElevenLabs) {
      try {
        const response = await fetch(
          `https://api.elevenlabs.io/v1/text-to-speech/${elevenLabsOptions.voiceId}`,
          {
            method: "POST",
            headers: {
              Accept: "audio/mpeg",
              "Content-Type": "application/json",
              "xi-api-key": elevenlabsApiKey,
            },
            body: JSON.stringify({
              text: content,
              model_id: elevenLabsOptions.modelId,
              voice_settings: elevenLabsOptions.voiceSettings,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`API Error: ${response.status}`);
        }

        const audioBlob = await response.blob();
        const audioUrlPlay = URL.createObjectURL(audioBlob);

        playAudio(audioUrlPlay, onEndCallback, true);
        return;
      } catch (error) {
        console.error("Error with ElevenLabs TTS:", error);
        browserSpeech(content, onEndCallback);
        return;
      }
    }

    browserSpeech(content, onEndCallback);
  };

  const browserSpeech = (text, onEndCallback) => {
    stopSpeaking();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "es-ES";
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => {
      setIsSpeaking(false);
      if (onEndCallback) onEndCallback();
    };
    utterance.onerror = () => setIsSpeaking(false);

    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = "";
      audioRef.current.load();
      audioRef.current = null;
    }

    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }

    setIsSpeaking(false);
  };

  return {
    // Props
    isSpeaking,
    isSupported: true,

    // Methods
    speakText,
    stopSpeaking,
  };
};
